import { kebabCase } from "lodash";
import React, { useState, useEffect } from "react";
import { GroupBase, StylesConfig } from "react-select";
import {
  FilterMultiSelectProps,
  MenuItem,
  MenuItems,
  CONTROL_HEIGHT
} from "./@types";
import SelectWithPaging from "./form/select/SelectWithPaging";
import { selectComponentsWithTestIds } from "./form/select/selectComponentsWithTestIds";

const FilterMultiSelect: React.FC<FilterMultiSelectProps> = ({
  id,
  data,
  value,
  disabled,
  placeholder,
  onChange,
  containerStyle,
  containerClass,
  isValidNewOption,
  onCreateOption,
  formatCreateLabel,
  onInputChange,
  noOptionsMessage,
  filterOption,
  isLoading,
  height = "regular",
  errorMsg,
  isOptionDisabled,
  customDisabledMessage
}) => {
  const [touched, setTouched] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (errorMsg) {
      setTouched(false);
      setShowError(true);
    } else {
      setShowError(false);
    }
  }, [errorMsg]);

  const testIdComponents = selectComponentsWithTestIds<MenuItem, true>(
    id,
    isOptionDisabled,
    customDisabledMessage
  );

  const controlHeight = CONTROL_HEIGHT[height || "regular"];
  const customStyles: StylesConfig<MenuItem, true, GroupBase<MenuItem>> = {
    option: base => ({
      ...base,
      cursor: "pointer"
    }),
    control: base => ({
      ...base,
      cursor: "pointer",
      minHeight: controlHeight,
      borderColor: showError ? "#d00000" : base.borderColor
    }),
    input: base => ({
      ...base,
      overflow: "auto",
      msOverflowStyle: "none",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none"
      }
    }),
    multiValueRemove: (base, props) => ({
      ...base,
      display: props.selectProps.isDisabled ? "none" : base.display
    }),
    multiValueLabel: (base, props) => ({
      ...base,
      paddingRight: props.selectProps.isDisabled
        ? base.paddingLeft
        : base.paddingRight
    }),
    multiValue: base => ({
      ...base,
      width: "auto"
    }),
    valueContainer: base => ({
      ...base,
      maxHeight: "100px",
      overflowY: "auto"
    }),
    dropdownIndicator: base => ({
      ...base,
      color: disabled ? "#ccc" : "#343a40"
    }),
    clearIndicator: base => ({
      ...base,
      color: "#343a40"
    })
  };

  const handleChange = (menuItems: MenuItems) => {
    setTouched(true);
    setShowError(false);
    onChange(menuItems);
  };

  const handleCreateOption = (inputValue: string) => {
    setTouched(true);
    setShowError(false);
    onCreateOption?.(inputValue);
  };

  const loadOptions = (inputValue: string) =>
    data?.filter(item => item.label.includes(inputValue)) || ([] as MenuItems);

  return (
    <div
      id={id}
      data-testid={`${kebabCase(id)}-multi-select-filter`}
      className={`rl-filter-select__container ${containerClass ?? ""}`}
      style={containerStyle}
    >
      <SelectWithPaging<MenuItem, true>
        menuOptions={loadOptions}
        components={testIdComponents}
        isDisabled={disabled}
        isMulti
        value={value}
        placeholder={<>{placeholder}</>}
        styles={customStyles}
        onChange={handleChange}
        isValidNewOption={isValidNewOption}
        onCreateOption={handleCreateOption}
        onInputChange={onInputChange}
        formatCreateLabel={formatCreateLabel}
        noOptionsMessage={noOptionsMessage}
        filterOption={filterOption}
        isLoading={isLoading}
        errorMsg={errorMsg}
        touched={touched}
        isOptionDisabled={isOptionDisabled}
      />
    </div>
  );
};

export default FilterMultiSelect;
